import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'

/**
 * One row per metered AI call. Tokens + `rawUsd` are the canonical USD cost; the
 * remaining fields are a charged SNAPSHOT of the account's currency/FX/markup taken at
 * record time, so a later `CurrencyRate` change never rewrites the price of a past call.
 * Tenant-isolated via the inherited `tenant` field.
 */
@ObjectType()
@InputType('AiUsageRecordInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aiusagerecords' } })
export class AiUsageRecord extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('aiur') })
  declare _id: string

  /**
   * Generic owner of this usage — a `User._id` (User billing scope) or a `Tenant._id`
   * (Tenant billing scope), resolved by `resolveBillingOwnerId`. Kept as a raw string
   * rather than a `RelatedEntity` so the engine stays app-agnostic — it never references
   * a QuickScore- or TotalMilk-specific entity type.
   */
  @Field(() => String)
  @Prop({ type: String, required: true })
  ownerId: string

  /** Caller-supplied feature tag (e.g. `quickscore.processScan`). */
  @Field(() => String)
  @Prop({ type: String, required: true })
  feature: string

  /** The model actually used to serve the call (default or ad-hoc override). */
  @Field(() => String)
  @Prop({ type: String, required: true })
  model: string

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  inputTokens: number

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  outputTokens: number

  /** Raw USD cost from the model's canonical `ModelRate`, before FX/markup. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  rawUsd: number

  // --- Charged snapshot, taken at record time from the account's CurrencyRate. ---

  @Field(() => String)
  @Prop({ type: String, required: true })
  currency: string

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  unitsPerUsd: number

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  markupPct: number

  /** `rawUsd` converted to the account currency (before markup). */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  rawCcy: number

  /** `rawCcy` after markup — the amount actually charged. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  billedCcy: number

  /** Credits debited for this call (1 credit = 1 unit of the account currency; equals `billedCcy`). */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  credits: number

  /**
   * Deterministic idempotency key for a caller that may retry the same billable unit of
   * work (e.g. a BullMQ batch chunk: `${batchId}:${chunkIndex}`). A record already
   * persisted for this key means the caller already paid for it — `RecordAiUsage` no-ops
   * instead of double-billing on retry. Absent for call sites with no retry surface
   * (single-shot scans), hence sparse.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  chargeKey?: string

  /**
   * `billedCcy - rawCcy` — resolved by `AiUsageRecordResolver`, never persisted (no
   * `@Prop`, no migration). Nullable because TotalMilk hauler-api registers `AiUsageResolver`
   * but not `AiUsageRecordResolver`: with no resolver behind it there, the field must resolve
   * to null rather than throw a non-null-field violation.
   */
  @Field(() => Float, { nullable: true })
  markupCcy?: number
}
